<template>
  <v-container>
    <v-row justify="center" class="title primary--text"><h1>Using a computer cluster for testing a combustion reaction mechanism</h1></v-row>
    <v-row class="my-5">
      <h1 id="v2-introduction">Introduction</h1>
    </v-row>
    <v-row>
      <v-col>
      <p>
        In combustion chemistry research, it is a common task to test large mechanisms against experimental data.
        In our <a href="#/videos/video1">previous video</a>
        we have shown that using RKD-format XML files and the graphical user interface of our Optima++ code,
        a mechanism can be tested very quickly and easily on a modern multicore PC.
        This option is viable if we use less than one hundred spatially homogeneous experiments for testing.
        Ignition delay times measured in shock tubes or rapid compression machines, concentrations measured in perfectly stirred reactors,
        flow reactors or shock tubes are examples of experimental facilities that can be simulated as spatially homogeneous systems.
        If we use more than one hundred homogeneous experiments for testing, or consider also laminar burning velocity measurements or
        species profiles determined burner stabilized flames, then more computer power is needed that is offered by a desktop PC.
      </p>
      <p>
        Computer clusters available within the research groups or at a computer center provide enough computational power.
        However, these computers are usually not accessible via a graphical program interface and a command line approach is needed.
        Optima++ allows testing reaction mechanisms on remote computer clusters using a command line operation mode.
      </p>
      </v-col>
    </v-row>
    <v-row>
      <h1 id="v2-input">Creating an Optima++ input file</h1>
    </v-row>
    <v-row>
      <v-col>
      <p>
        In the first step, we will copy the Li-2007 mechanism and the xml files to the head computer of our cluster.
        Previously I downloaded 10 ignition delay time measurement xml files from the <a targer="_blank" href="http://respecth.chem.elte.hu/respecth/index.php">ReSpecTh</a> site.
      </p>

      <p>You can download these files from here:</p>
      <download-box title="download examples" file="video-2-example-files.zip" />
      <p>
        I use here the scp command for transferring these files:
      </p>

      <highlight-code lang="shell" class="code-block mb-6" :code="scp" />

        <p>
          On Windows, you can use <a target="_blank" href="https://winscp.net/eng/download.php">WinSCP</a> for this purpose.
        </p>

        <p>Now we can login to the server using the <code>ssh</code> command.</p>

        <highlight-code lang="shell" class="code-block mb-6" code="$ ssh head" />

        <extra-bits title="How to login to a cluster using Windows?" :markdown="clusterOnWindows" />

        <p>
          Optima++ can be installed on the cluster by copying the commandline binaries to the cluster computers.
        </p>

        <p>
          To check if Optima++ is working correctly,
          we may run it with the <code>-v</code> argument.
          It will list the available solvers.
        </p>

        <highlight-code lang="shell" class="code-block mb-6" code="tutorials/$ bin/Release/OptimaPP -v" />
        <highlight-code lang="plaintext" class="code-block mb-6" :code="oppOut" />

        <p>
           The input file for Optima++ is a simple text file so you can use any text editor.
        </p>
        <highlight-code lang="optimapp" class="code-block mb-6" :code="inputFile" />
        <p>
          Of course, the input file can be prepared on the host computer and when it is done it can be uploaded to the server.
          In the video, the <a target="_blank" href="https://neovim.io/">neovim</a> text editor was used. On Windows, we recommend to use
          <a target="_blank" href="https://notepad-plus-plus.org/downloads/">Notepad++</a> instead of the default notepad.
        </p>
        <!-- TODO: Add syntax files. -->
        <p>
          For each job in Optima++, there is a corresponding input block.
          An input block begins with the name of the job and it is closed with the <code>END</code> keyword.
          The name of the input block for mechanism testing is <code>MECHTEST</code>.
          However, the <code>MECHTEST</code> job requires a compiled mechanism.
          This is why there is a <code>MECHMOD</code> block first.
        </p>
        <p>
          The <code>MECHMOD</code> block can be used for mechanism compilation i.e.
          converting the mechanism into the internal format of the solver
          or for the modification of the mechanism
          for example for removing or renaming species.
        </p>
        <p>
          In the first step, we assigned an identifier for our mechanism in the <code>MECHMOD</code> block with the <code>USE_NAME</code> command.
          This name will be used in the other blocks as a reference to that mechanism.
          In the next line, we can enter the relative path to the mechanism file.
        </p>
        <p>
          The next command is for mechanism compilation.
          We can compile the mechanism either with FlameMaster or OpenSMOKE++.
          The <code>COMPILE_FM</code> command requires a path name where the compiled mechanism will be saved.
        </p>
        <p>
          Now we can jump into the <code>MECHTEST</code> block.
          We set up the <code>Li-2007</code> mechanism to be used with the <code>MECHANISM</code> keyword.
          In the next line, we instruct Optima++ to set the <code>THREAD_LIMIT</code> to 48.
          The THREAD_LIMIT is the maximum number of the simultaneously running FlameMaster simulations on the cluster.
        </p>
        <p>
          Finally, we can enter the relative path to the selected xml files after the <code>NAME</code> keyword.
          Optionally, we can provide the standard deviation of the experimental data after the <code>SIGM</code> keyword.
          This triggers the evaluation of the error function and adds error bars to the final plots.
        </p>
        <p>
          After the <code>SIGM</code> keyword, we have to define the scale of the standard deviation (<code>SIGMSCALE</code>).
          It is <code>abs</code> for absolute and <code>absln</code> for relative errors.
          I assigned here 10% relative error to each point in each xml file.
          Now, we are ready to run Optima++.
        </p>
      </v-col>
    </v-row>
    <v-row>
      <h1 id="v2-run">Running Optima++</h1>
    </v-row>
    <v-row>
      <v-col>
        <p>
          To run Optima++, we have to pass the name of the input file as a command line parameter.
          The actual command how to send a job to the cluster computer depends on the software architecture of the cluster.
          Here I use a bash script which was written to send Optima++ jobs to our cluster.
          If you are planning to use Optima++ on a different type of cluster, please consult the cluster’s documentation for details.
        </p>
        <p>
          Using computer clusters, you may run simulations for several days on several hundred cores in parallel.
          You may check the progress of the calculations on the cluster and when all calculations have been finished,
          you may process and visualize the simulation results on the host PC.
        </p>
      </v-col>
    </v-row>
    <v-row>
      <h1 id="v2-proc">Processing the simulation results</h1>
    </v-row>
    <v-row>
      <v-col>
        <p>
          The result files are saved to the <code>outputs/{input file name}</code> folder.
          The simulation results are saved to a simple text file called <code>mechTestResults</code> and
          these results are also saved to a CSV file,
          which can be directly imported to Excel or to any spreadsheet program for easier postprocessing.
        </p>
        <p>
          On a linux PC, you can use the <code>scp</code> command to download the results folder.
        </p>
        <highlight-code lang="shell" class="code-block mb-6" code="tutorials/$ scp -r head:/partition1/tutorials/outputs/mechtest-Li2007.opp outputs/" />
        <p>
          On Windows, WinSCP can be used.
        </p>
        <p>
          Optima++ can detect if GnuPlot is available on the host computer.
          If it is installed, Optima++ will generate comparison figures for each xml file referenced in the input file.
          These figures are available in the <code>plots</code> folder.
        </p>
        <image-viewer :src="require('@/assets/v2-plots-folder.png')" :small-width="500" />
        <p>
          We can also use the graphical interface of Optima++ to analyze the downloaded results.
          Just simply drag and drop the <code>mechTestResults</code> file to the main window of the program.
          See the <router-link to="/videos/video1?to=v1-results" replace>Viewing the results of mechanism testing</router-link> section of the first video for details.
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <p>
          This was a demonstration that simulations with large number of experimental data points or flame simulations requiring
          much computer time can be carried out in an effective way on a computer cluster using RKD format XML files and
          the command line operation mode of Optima++.
        </p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import DownloadBox from '@/components/DownloadBox'
import ExtraBits from '@/components/ExtraBits'
import ImageViewer from '@/components/ImageViewer'
import clusterWin from '@/views/bits/clusterWin.md'
import OptimaOutput from '@/assets/optimaOutput.txt'
import optimaInput from '@/assets/optimaInput.txt'
export default {
  name: "Video2",
  components: {DownloadBox, ExtraBits, ImageViewer},
  data: () => {
    return {
      scp: "$ scp -r mechanisms head:/partition1/tutorials/\n$ scp -r xmls head:/partition1/tutorials/",
      clusterOnWindows: clusterWin,
      oppOut: OptimaOutput,
      inputFile: optimaInput
    }
  },
  methods: {
    bookMarks () {
      return [
        {
          id: '#v2-introduction',
          title: 'Introduction'
        },
        {
          id: '#v2-input',
          title: 'Input files'
        },
        {
          id: '#v2-proc',
          title: 'Simulation results'
        },
      ]
    }
  }
}
</script>

<style scoped>

</style>